import React, { useEffect, useState } from 'react';
import { IInvoice, IInvoiceFilter } from '../interface/IInvoice';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../helper/ToastProvider';
import Breadcrumb from '../components/Breadcrumb';
import { Box, Button, Sheet, Stack, Typography } from '@mui/joy';
import invoiceApi from '../api/invoice';
import InvoicePaymentModal from '../components/InvoicePaymentModal';
import InvoiceTable from '../components/InvoiceTable';
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded';
import { styled } from '@mui/joy/styles';
import InvoiceTableFilter from '../components/InvoiceTableFilter';
import { deutschNumberFormatter } from '../helper/utils';

type Props = {};

const Item = styled(Sheet)(({ theme }) => ({
  ...theme.typography['body-sm'],
  textAlign: 'center',
  fontWeight: theme.fontWeight.md,
  color: theme.vars.palette.text.secondary,
  border: '1px solid',
  borderColor: theme.palette.divider,
  padding: theme.spacing(1),
  borderRadius: theme.radius.md,
}));

const Invoices = (props: Props) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortBy, setSortBy] = useState<keyof IInvoice | undefined>();
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [invoices, setInvoices] = useState<IInvoice[]>();
  const [filters, setFilters] = useState<IInvoiceFilter>({
    externalInvoices: true,
  });
  const [offerIds, setOfferIds] = useState<string[] | undefined | null>();
  const [selectedInvoice, setSelectedInvoice] = useState<null | IInvoice>(null);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [totalNoInvoice, setTotalNoInvoice] = useState(0);
  const [totalNet, setTotalNet] = useState<string>();
  const [totalGross, setTotalGross] = useState<string>();

  useEffect(() => {
    getInvoicesPaginated(page, rowsPerPage, { ...filters, sortedBy: sortBy, sortedOrder: sortOrder });
  }, [page, rowsPerPage, sortBy, sortOrder]);

  const getInvoicesPaginated = async (_page: number, _limit: number, _filters: IInvoiceFilter) => {
    try {
      setLoading(true);
      const res = await invoiceApi.getInvoicesPaginated(_page, _limit, _filters);
      setInvoices(res.data.data);
      setTotalNoInvoice(res.data.total);
      setTotalNet(res.data.totalNetAmount);
      setTotalGross(res.data.totalGrossAmount);
      setOfferIds(res.data.offerIds);
      setLoading(false);
    } catch (e) {
      showToast({
        message: 'Invoice Fetch Failed',
        severity: 'danger',
      });
      setLoading(false);
    }
  };
  const downloadXml = async (invoiceId: string) => {
    try {
      const response = await invoiceApi.downloadXmlFile(invoiceId);
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition.split(';')[1].split('=')[1].replace(/"/g, '');
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading Xml:', error);
    }
  };

  const downloadExcel = async (invoiceId: string) => {
    try {
      const response = await invoiceApi.downloadExcelFile(invoiceId);
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition.split(';')[1].split('=')[1].replace(/"/g, '');
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading Excel:', error);
    }
  };

  const onDownloadPdf = async (invoiceId: string) => {
    try {
      const response = await invoiceApi.downloadInvoicePdfFile(invoiceId);
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition.split(';')[1].split('=')[1].replace(/"/g, '');
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading Pdf:', error);
    }
  };

  const onDownloadPdfWithoutVat = async (invoiceId: string) => {
    try {
      const response = await invoiceApi.downloadInvoiceWithoutVatPdfFile(invoiceId);
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition.split(';')[1].split('=')[1].replace(/"/g, '');
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading Pdf:', error);
    }
  };

  const sendInvoiceEmail = async (invoiceId: string) => {
    try {
      await invoiceApi.sendInvoiceEmail(invoiceId);
      getInvoicesPaginated(page, rowsPerPage, { sortedBy: sortBy, sortedOrder: sortOrder });
      showToast({
        message: 'Email Send Success',
        severity: 'success',
      });
    } catch (e) {
      showToast({
        message: 'Email Send Failed',
        severity: 'danger',
      });
    }
  };

  const onFilterChange = (filter: keyof IInvoiceFilter, value: any) => {
    const updatedFilters = {
      ...filters,
      [filter]: value === '' || value === 'all' ? null : value,
    };
    const activeFilters = Object.fromEntries(Object.entries(updatedFilters).filter(([_, v]) => v !== null && v !== undefined && v !== ''));
    setFilters(activeFilters);
  };

  const onFilterApply = () => {
    setPage(0);
    getInvoicesPaginated(0, rowsPerPage, { ...filters, sortedBy: sortBy, sortedOrder: sortOrder });
  };

  const onFilterReset = () => {
    setFilters({
      externalInvoices: true,
    });
    setPage(0);
    getInvoicesPaginated(0, rowsPerPage, { externalInvoices: true, sortedBy: sortBy, sortedOrder: sortOrder });
  };

  const onPayment = (selInvoice: IInvoice) => {
    setSelectedInvoice(selInvoice);
    setOpenPaymentModal(true);
  };

  const closePaymentModal = () => {
    setSelectedInvoice(null);
    setOpenPaymentModal(false);
  };

  const onPaymentSubmit = async (invoiceId: string, dateOfPayment: string) => {
    try {
      const res = await invoiceApi.addPaymentDate(invoiceId, dateOfPayment);
      setSubmittingPayment(true);
      closePaymentModal();
      getInvoicesPaginated(page, rowsPerPage, { sortedBy: sortBy, sortedOrder: sortOrder });
      showToast({
        message: 'Payment Add Success',
        severity: 'success',
      });
    } catch (e) {
      showToast({
        message: 'Payment Add Failed',
        severity: 'danger',
      });
    } finally {
      setSubmittingPayment(false);
    }
  };
  return (
    <>
      <Breadcrumb crumb={['Invoices']} />
      <Box
        sx={{
          display: 'flex',
          mb: 1,
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'start', sm: 'center' },
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Typography level='h2' component='h1'>
          Invoices
        </Typography>
        <Button color='primary' onClick={() => navigate('newinvoice')} startDecorator={<PersonAddRoundedIcon />} size='sm'>
          Create New
        </Button>
      </Box>
      {invoices && (
        <>
          <InvoiceTableFilter
            filters={filters}
            offerIds={offerIds!}
            onFilterChange={onFilterChange}
            onFilterApply={onFilterApply}
            onFilterReset={onFilterReset}
          />
          <Box sx={{ width: '100%' }}>
            <Stack spacing={2} direction={'row'} sx={{ alignItems: 'center' }}>
              <Item>Invoices: {totalNoInvoice}</Item>
              <Item>Total Net: {deutschNumberFormatter(totalNet || 0)} €</Item>
              <Item>Total Gross: {deutschNumberFormatter(totalGross || 0)} €</Item>
            </Stack>
          </Box>
          <InvoiceTable
            loading={loading}
            order={sortOrder}
            setOrder={setSortOrder}
            orderBy={sortBy}
            setOrderBy={setSortBy}
            setPage={setPage}
            page={page}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            invoices={invoices}
            total={totalNoInvoice}
            onDownloadXml={downloadXml}
            onDownloadExcel={downloadExcel}
            onDownloadPdf={onDownloadPdf}
            onDownloadPdfWithoutVat={onDownloadPdfWithoutVat}
            sendInvoiceEmail={sendInvoiceEmail}
            onPayment={onPayment}
          />
        </>
      )}
      <InvoicePaymentModal
        open={openPaymentModal}
        onClose={closePaymentModal}
        onSubmit={onPaymentSubmit}
        submitting={submittingPayment}
        invoice={selectedInvoice}
      />
    </>
  );
};

export default Invoices;
